import { Link } from "react-router-dom";
import { Banknote, Clock, TrendingUp, Shield, Sparkles, GraduationCap } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { fmtFJD } from "@/lib/api";

const riskColors = {
    low: "bg-fiji-green/10 text-fiji-green border-fiji-green/30",
    medium: "bg-coral/10 text-coral border-coral/30",
    high: "bg-destructive/10 text-destructive border-destructive/30",
};

export default function JVCard({ jv }) {
    const isBoosted = jv.is_boosted && jv.boost_active !== false && (
        !jv.boost_expires_at || new Date(jv.boost_expires_at) > new Date()
    );
    return (
        <Link to={`/jv/${jv.id}`} data-testid={`jv-card-${jv.id}`} className="group block relative">
            {jv.is_premium && (
                <div className="absolute -top-2 -right-2 z-10 bg-sunset text-white text-[10px] font-bold uppercase tracking-wider font-app px-3 py-1 rounded-full shadow-lg flex items-center gap-1" data-testid={`premium-ribbon-${jv.id}`}>
                    <Sparkles className="h-3 w-3" /> Premium
                </div>
            )}
            {!jv.is_premium && isBoosted && (
                <div className="absolute -top-2 -right-2 z-10 bg-fiji-green text-white text-[10px] font-bold uppercase tracking-wider font-app px-3 py-1 rounded-full shadow-lg flex items-center gap-1" data-testid={`boost-ribbon-${jv.id}`}>
                    <Sparkles className="h-3 w-3" /> Boosted
                </div>
            )}
            <Card className={`bg-sand/40 dark:bg-secondary/50 border-l-4 ${jv.is_premium ? "border-coral ring-2 ring-coral/30" : isBoosted ? "border-fiji-green ring-1 ring-fiji-green/30" : "border-coral"} p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                    <Badge className="bg-turquoise/20 text-ocean dark:text-turquoise border-0 font-app uppercase tracking-wider text-[10px]">
                        {jv.project_type}
                    </Badge>
                    <div className="flex items-center gap-1.5">
                        {jv.owner_agent_trained && (
                            <Badge
                                data-testid={`certified-jv-${jv.id}`}
                                className="bg-coral text-white border-0 font-app uppercase tracking-wider text-[10px] flex items-center gap-1"
                                title="Sourced by a Certified Bula Agent"
                            >
                                <GraduationCap className="h-3 w-3" /> Certified
                            </Badge>
                        )}
                        <Badge variant="outline" className={`font-app text-[10px] uppercase ${riskColors[jv.risk_profile] || ""}`}>
                            {jv.risk_profile} risk
                        </Badge>
                    </div>
                </div>
                <h3 className="font-display text-xl font-bold leading-tight mb-2 group-hover:text-ocean dark:group-hover:text-turquoise">
                    {jv.title}
                </h3>
                <p className="text-sm text-muted-foreground font-app line-clamp-2 mb-4">
                    {jv.project_vision}
                </p>
                <div className="grid grid-cols-2 gap-3 text-xs font-app">
                    <div className="bg-background/60 rounded-md p-3">
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                            <Banknote className="h-3 w-3" /> Capital
                        </div>
                        <div className="font-semibold text-foreground">{fmtFJD(jv.capital_required_fjd)}</div>
                    </div>
                    <div className="bg-background/60 rounded-md p-3">
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                            <TrendingUp className="h-3 w-3" /> ROI Est.
                        </div>
                        <div className="font-semibold text-fiji-green">{jv.roi_estimate_pct}%</div>
                    </div>
                    <div className="bg-background/60 rounded-md p-3">
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                            <Clock className="h-3 w-3" /> Timeline
                        </div>
                        <div className="font-semibold text-foreground">{jv.timeline_months} mo</div>
                    </div>
                    <div className="bg-background/60 rounded-md p-3">
                        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
                            <Shield className="h-3 w-3" /> Equity
                        </div>
                        <div className="font-semibold text-foreground">{jv.landowner_equity_pct}% landowner</div>
                    </div>
                </div>
                <div className="mt-4 text-xs text-muted-foreground font-app flex items-center justify-between">
                    <span>{jv.location_region}, {jv.location_island}</span>
                    <span className="text-coral font-semibold uppercase tracking-wider text-[10px]">View deal →</span>
                </div>
            </Card>
        </Link>
    );
}
